import { showToast } from '../Common/Toast';
import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useTheme } from '../../contexts/ThemeContext';
import TimeSelect from '../Common/TimeSelect';
import { supabase } from '../../utils/supabaseClient';
import {
  buildBusinessHoursFromLegacy,
  normalizeBusinessHours,
  deriveLegacyFromBusinessHours,
  DAY_NAMES
} from '../../utils/smartScheduling';
import {
  CogIcon,
  ClockIcon,
  BellIcon,
  CheckCircleIcon,
  MoonIcon,
  SunIcon
} from '@heroicons/react/24/outline';

import smsNumberService from '../../services/SmsNumberService';
// Day index convention matches SchedulingSettingsTab and JS Date.getDay():
// 0 = Sunday, 1 = Monday, ..., 6 = Saturday.
const WEEK_DAYS = [
  { index: 0, name: 'Sunday', short: 'Sun' },
  { index: 1, name: 'Monday', short: 'Mon' },
  { index: 2, name: 'Tuesday', short: 'Tue' },
  { index: 3, name: 'Wednesday', short: 'Wed' },
  { index: 4, name: 'Thursday', short: 'Thu' },
  { index: 5, name: 'Friday', short: 'Fri' },
  { index: 6, name: 'Saturday', short: 'Sat' }
];

// Existing rows in company_settings may have working_days as either an integer
// array (the canonical shape SchedulingSettingsTab writes) or the
// {monday:true,...} object that the column's schema default seeds at company
// creation. smartScheduling.js has its own normalizer; this one matches it so
// the wizard reads either shape and writes the integer-array shape.
function normalizeWorkingDays(raw) {
  if (Array.isArray(raw)) {
    return raw.filter(n => Number.isInteger(n) && n >= 0 && n <= 6).sort((a, b) => a - b);
  }
  if (typeof raw === 'string') {
    try { return normalizeWorkingDays(JSON.parse(raw)); } catch { /* fall through */ }
  }
  if (raw && typeof raw === 'object') {
    const dayNameToIndex = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
    return Object.entries(raw)
      .filter(([, v]) => v === true)
      .map(([k]) => dayNameToIndex[String(k).toLowerCase()])
      .filter(n => Number.isInteger(n))
      .sort((a, b) => a - b);
  }
  return [1, 2, 3, 4, 5];
}

const BusinessPreferencesStep = ({ onComplete, onValidationChange, registerSave }) => {
  const { user } = useUser();
  const { themeMode, setThemeMode } = useTheme();
  const [preferences, setPreferences] = useState({
    business_hours_start: '08:00',
    business_hours_end: '17:00',
    working_days: [1, 2, 3, 4, 5],
    job_buffer_minutes: 30,
    max_advance_booking_days: 90,
    email_notifications_enabled: true,
    sms_notifications_enabled: false
  });
  // The canonical per-day hours, carried through untouched unless the contractor edits
  // the single start/end on this screen. This step can only SHOW one hours range, but a
  // company may already have per-day hours set in Settings (Jerry's closes at 14:00 on
  // Saturday). Rebuilding business_hours from this step's single range flattened those.
  const [canonicalHours, setCanonicalHours] = useState(null);
  const [hoursEdited, setHoursEdited] = useState(false);
  // Same rule for the buffer. The scheduler obeys default_buffer_before/after_minutes;
  // job_buffer_minutes is a derived copy (Smart Scheduling saves it as max(before, after)) that
  // had drifted for 301 of 333 companies (2026-10-29). This step shows max(before, after) and
  // writes the buffers only when the contractor edits the field (or there is no row yet), so
  // passing through the step no longer changes the real buffer.
  const [bufferEdited, setBufferEdited] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(themeMode || 'dark');
  // SMS needs a provisioned number before anything can send. Only 1 company has one
  // live (2026-08-28), so for almost everyone this toggle was a switch wired to nothing.
  const [smsNumber, setSmsNumber] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!user?.company_id) return undefined;
    // Only a LIVE number (active + verified) can carry traffic — same rule as
    // NotificationsSettingsTab. A bought-but-unverified number keeps the toggle off (#4784).
    smsNumberService.getTier(user.company_id)
      .then(t => { if (!cancelled) setSmsNumber(t.live ? t.number : null); })
      .catch(() => { if (!cancelled) setSmsNumber(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.company_id]);

  useEffect(() => {
    loadPreferences();
  }, [user?.company_id]);

  const loadPreferences = async () => {
    if (!user?.company_id) return;

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('company_settings')
        .select('business_hours, business_hours_start, business_hours_end, working_days, default_buffer_before_minutes, default_buffer_after_minutes, max_advance_booking_days, email_notifications_enabled, sms_notifications_enabled')
        .eq('company_id', user.company_id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (!data) setBufferEdited(true);
      if (data) {
        // CANONICAL FIRST. business_hours is the source of truth (Settings treats it that
        // way and derives the legacy columns from it); this step used to do the reverse and
        // read the legacy pair, which is an ENVELOPE across open days -- earliest start,
        // latest end -- not the hours of any particular day. Reading it as if it were the
        // daily hours is the same mistake that offered 07:00 slots to a company open 08:00.
        const bh = data.business_hours && typeof data.business_hours === 'object'
          ? normalizeBusinessHours(data.business_hours)
          : null;
        setCanonicalHours(bh);

        const legacy = bh ? deriveLegacyFromBusinessHours(bh) : null;

        setPreferences(prev => ({
          ...prev,
          business_hours_start: (legacy?.business_hours_start || data.business_hours_start || prev.business_hours_start).slice(0, 5),
          business_hours_end: (legacy?.business_hours_end || data.business_hours_end || prev.business_hours_end).slice(0, 5),
          working_days: (bh
            ? DAY_NAMES.reduce((acc, name, idx) => (bh[name]?.open ? [...acc, idx] : acc), [])
            : normalizeWorkingDays(data.working_days)) ?? prev.working_days,
          job_buffer_minutes: (data.default_buffer_before_minutes == null && data.default_buffer_after_minutes == null)
            ? prev.job_buffer_minutes
            : Math.max(Number(data.default_buffer_before_minutes) || 0, Number(data.default_buffer_after_minutes) || 0),
          max_advance_booking_days: data.max_advance_booking_days ?? prev.max_advance_booking_days,
          email_notifications_enabled: data.email_notifications_enabled ?? prev.email_notifications_enabled,
          sms_notifications_enabled: data.sms_notifications_enabled ?? prev.sms_notifications_enabled
        }));
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const validatePreferences = () => {
    const warnings = [];
    if (!preferences.working_days || preferences.working_days.length === 0) {
      warnings.push('Pick at least one working day so customers know when you\'re available');
    }
    onValidationChange?.({ valid: true, errors: [], warnings });
    return true;
  };

  useEffect(() => {
    if (!loading) validatePreferences();
  }, [preferences, loading]);

  const toggleWorkingDay = (dayIndex) => {
    setPreferences(prev => ({
      ...prev,
      working_days: prev.working_days.includes(dayIndex)
        ? prev.working_days.filter(d => d !== dayIndex)
        : [...prev.working_days, dayIndex].sort((a, b) => a - b)
    }));
  };

  const updatePreference = (field, value) => {
    // Editing the hours is what gives this screen permission to overwrite per-day times.
    // Merely PASSING THROUGH the step must not: it can only display one range, so applying
    // that range to every day would silently discard a Saturday that closes early.
    if (field === 'business_hours_start' || field === 'business_hours_end') {
      setHoursEdited(true);
    }
    if (field === 'job_buffer_minutes') setBufferEdited(true);
    setPreferences(prev => ({ ...prev, [field]: value }));
  };

  const savePreferences = async () => {
    if (!user?.company_id) return;

    try {
      setSaving(true);

      if (selectedTheme !== themeMode) {
        setThemeMode(selectedTheme);
      }

      // Payload uses the actual company_settings column names. Earlier
      // versions of this step spread an in-memory state with invented field
      // names (business_hours, buffer_time, advance_booking_days, ...) which
      // caused PostgREST PGRST204 ("column not in schema cache") and trapped
      // the wizard at this step (TKT-0001).
      // CANONICAL business_hours is built first, then the legacy columns are DERIVED from
      // it -- the same direction Settings writes in. This step used to do the opposite,
      // treating its own single start/end as the truth and rebuilding business_hours from
      // it, so two writers disagreed about which column was canonical.
      //
      // NON-DESTRUCTIVE. The screen can only show ONE hours range, so it must not assume
      // every day uses it. Days keep their existing per-day times unless the contractor
      // actually edited the start/end here; only the open/closed flag follows the day
      // toggles. Without this, opening this step flattened a company that closes at 14:00
      // on Saturday to a single range across the week -- data loss caused by a screen that
      // could not even display what it was overwriting.
      const openDays = new Set(preferences.working_days || []);
      const base = canonicalHours || buildBusinessHoursFromLegacy(
        preferences.working_days,
        preferences.business_hours_start,
        preferences.business_hours_end
      );
      const nextHours = {};
      DAY_NAMES.forEach((name, idx) => {
        const existing = base[name] || {};
        nextHours[name] = {
          open: openDays.has(idx),
          start: hoursEdited || !existing.start ? preferences.business_hours_start : existing.start,
          end: hoursEdited || !existing.end ? preferences.business_hours_end : existing.end
        };
      });
      const legacy = deriveLegacyFromBusinessHours(nextHours);

      const payload = {
        company_id: user.company_id,
        // Derived, not authored. The legacy pair is an ENVELOPE across open days, which is
        // why writing this step's single range into it directly is what produced 07:00
        // slots for a company that opens at 08:00.
        business_hours_start: legacy.business_hours_start,
        business_hours_end: legacy.business_hours_end,
        working_days: legacy.working_days,
        business_hours: nextHours,
        // A cleared field is empty, not zero — coerce at the save boundary.
        //
        // Write all THREE buffer columns. The scheduler reads
        // default_buffer_before/after_minutes (Calendar.js, SmartSchedulingAssistant), and
        // getSchedulingSettings only falls back to job_buffer_minutes when those are FALSY —
        // but they carry a column default of 15, which is truthy, so the fallback never
        // fires. Writing job_buffer_minutes alone meant the contractor's chosen gap was
        // stored, displayed, and then ignored: verified live 2026-08-30, all 210 companies
        // have before/after = 15 whatever they picked.
        //
        // One question in the UI ("buffer between jobs") maps to both sides, which is
        // correct — Calendar takes Math.max(before, after), so both = N yields an N-minute
        // gap rather than 2N.
        //
        // Only when edited (see bufferEdited): otherwise the before/after the contractor set in
        // Smart Scheduling are left exactly as they are.
        ...(bufferEdited ? {
          job_buffer_minutes: Number(preferences.job_buffer_minutes) || 0,
          default_buffer_before_minutes: Number(preferences.job_buffer_minutes) || 0,
          default_buffer_after_minutes: Number(preferences.job_buffer_minutes) || 0
        } : {}),
        max_advance_booking_days: preferences.max_advance_booking_days,
        email_notifications_enabled: preferences.email_notifications_enabled,
        sms_notifications_enabled: preferences.sms_notifications_enabled,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('company_settings')
        .upsert(payload, { onConflict: 'company_id' });

      if (error) throw error;

      onComplete?.(preferences);
    } catch (error) {
      console.error('Error saving preferences:', error);
      showToast('error', 'Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Let the wizard's footer "Continue" button save this step too (not just the
  // in-form "Save Preferences & Continue" button) — otherwise footer-Continue
  // drops the form data silently.
  //
  // OBSOLETE AS OF 2026-09-08, kept only to stop the old reasoning being reintroduced:
  // OnboardingWizard no longer clears stepSaveRef in an effect. It tags each registration
  // with the step that made it and ignores one from a different step, so the child/parent
  // effect ORDER no longer matters and a useCallback here would now be safe. The old note
  // read: do not memoize, because the parent nulls a stable callback registered once. That
  // hazard was real and is gone. A fresh identity each render is
  // what makes this re-register on the following render. Same note in CompanyBasicsStep.js,
  // which had exactly this bug until d23039b60 (F-ATLAS-0812-1609-STEP1-NO-PATCH).
  useEffect(() => { registerSave?.(savePreferences); }, [registerSave, savePreferences]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading preferences...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <CogIcon className="w-12 h-12 text-orange-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Business Preferences</h2>
        <p className="text-gray-600 dark:text-gray-400">
          Set your operating hours, scheduling defaults, and notification preferences.
        </p>
      </div>

      <div className="space-y-6">
        {/* Business Hours */}
        <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <ClockIcon className="w-5 h-5 text-orange-600" />
            Business Hours
          </h3>

          <div className="flex flex-wrap items-center gap-4 mb-4">
            {/* Board #400: a themed dropdown, not <input type="time">. Its browser-drawn popup
                stayed white even with color-scheme: dark verified live on the input — see
                components/Common/TimeSelect. */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Open</label>
              <TimeSelect
                aria-label="Opening time"
                value={preferences.business_hours_start}
                onChange={(v) => updatePreference('business_hours_start', v)}
                className="px-3 py-1 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <span className="text-gray-500">to</span>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Close</label>
              <TimeSelect
                aria-label="Closing time"
                value={preferences.business_hours_end}
                onChange={(v) => updatePreference('business_hours_end', v)}
                className="px-3 py-1 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Days you're open
            </label>
            <div className="flex flex-wrap gap-2">
              {WEEK_DAYS.map(day => {
                const isOpen = preferences.working_days.includes(day.index);
                return (
                  <button
                    key={day.index}
                    type="button"
                    onClick={() => toggleWorkingDay(day.index)}
                    aria-pressed={isOpen}
                    title={isOpen ? `${day.name} — open` : `${day.name} — closed`}
                    className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                      isOpen
                        ? 'bg-orange-500/20 border-orange-400 text-orange-700 dark:text-orange-300 font-semibold ring-1 ring-orange-400'
                        : 'bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 text-gray-400 dark:text-gray-500 font-normal hover:border-orange-300'
                    }`}
                  >
                    {day.short}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Scheduling Preferences */}
        <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Scheduling Preferences</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Buffer between jobs (minutes)
              </label>
              <input
                type="number"
                value={preferences.job_buffer_minutes ?? ''}
                onChange={(e) => updatePreference('job_buffer_minutes', e.target.value === '' ? '' : (parseInt(e.target.value) || 0))}
                className="w-full px-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-orange-500"
                min="0"
                step="5"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Max advance booking (days)
              </label>
              <input
                type="number"
                value={preferences.max_advance_booking_days}
                onChange={(e) => updatePreference('max_advance_booking_days', parseInt(e.target.value) || 90)}
                className="w-full px-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-orange-500"
                min="1"
              />
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <BellIcon className="w-5 h-5 text-orange-600" />
            Notifications
          </h3>
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={preferences.email_notifications_enabled}
                onChange={(e) => updatePreference('email_notifications_enabled', e.target.checked)}
                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Email notifications</span>
            </label>
            <label className={`flex items-center gap-2 ${smsNumber ? '' : 'opacity-60 cursor-not-allowed'}`}>
              <input
                type="checkbox"
                disabled={!smsNumber}
                // #6251: show the SAVED choice (disabled until there is a number), as Settings ->
                // Notifications does. It read OFF whenever there was no number while the stored
                // value (column default) is on.
                checked={!!preferences.sms_notifications_enabled}
                onChange={(e) => updatePreference('sms_notifications_enabled', e.target.checked)}
                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 disabled:cursor-not-allowed"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">SMS notifications</span>
            </label>
            {!smsNumber && (
              <p className="text-xs text-gray-500 dark:text-slate-400 ml-6 -mt-1">
                This shows your saved choice, but no automatic texts go out until your own number is
                active. Set up SMS in Settings → Billing &amp; Subscription.
              </p>
            )}
          </div>
        </div>

        {/* Theme Preference */}
        <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <SunIcon className="w-5 h-5 text-orange-600" />
            Theme Preference
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Choose your preferred theme. You can change this anytime in your settings.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              type="button"
              onClick={() => setSelectedTheme('dark')}
              className={`p-4 rounded-lg border-2 transition-all ${
                selectedTheme === 'dark'
                  ? 'border-orange-600 bg-orange-50 dark:bg-orange-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-orange-300'
              }`}
            >
              <div className="flex items-center justify-center mb-3">
                <MoonIcon className="w-8 h-8 text-orange-600" />
              </div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-1">Dark</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Easy on the eyes</p>
            </button>

            <button
              type="button"
              onClick={() => setSelectedTheme('light')}
              className={`p-4 rounded-lg border-2 transition-all ${
                selectedTheme === 'light'
                  ? 'border-orange-600 bg-orange-50 dark:bg-orange-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-orange-300'
              }`}
            >
              <div className="flex items-center justify-center mb-3">
                <SunIcon className="w-8 h-8 text-orange-600" />
              </div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-1">Light</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Bright and clean</p>
            </button>

            <button
              type="button"
              onClick={() => setSelectedTheme('system')}
              className={`p-4 rounded-lg border-2 transition-all ${
                selectedTheme === 'system'
                  ? 'border-orange-600 bg-orange-50 dark:bg-orange-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-orange-300'
              }`}
            >
              <div className="flex items-center justify-center mb-3">
                <CogIcon className="w-8 h-8 text-orange-600" />
              </div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-1">System</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Match your device</p>
            </button>
          </div>
        </div>
      </div>

      {/* Continue Button */}
      <div className="text-center mt-8">
        <button
          type="button"
          onClick={savePreferences}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white font-medium rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Saving...
            </>
          ) : (
            <>
              <CheckCircleIcon className="w-5 h-5" />
              Save Preferences & Continue
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default BusinessPreferencesStep;
